import {
	ChangeDetectionStrategy,
	Component,
	ModelSignal,
	OnDestroy,
	OnInit,
	WritableSignal,
	inject,
	model,
	signal,
} from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatStepperModule } from "@angular/material/stepper";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { CmsBlockKey, CmsService, WebviewerAnnotationHelpCmsBlock } from "libs/cms/src/lib";
import { FormsModule } from "@angular/forms";

/**
 * Local storage key that stores whether users opted out from automatic help dialog opening.
 */
export const DO_NOT_SHOW_HELP_STORAGE_KEY = "doNotShowWebviewerAnnotationHelp";

@Component({
	selector: "lib-webviewer-annotation-help-dialog",
	templateUrl: "./webviewer-annotation-help-dialog.component.html",
	styleUrls: ["./webviewer-annotation-help-dialog.component.scss"],
	imports: [
		MatDialogModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatStepperModule,
		MatCardModule,
		MatCheckboxModule,
		FormsModule,
		TranslatePipe,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebviewerAnnotationHelpDialogComponent implements OnInit, OnDestroy {
	private cmsService = inject(CmsService);
	private translateService = inject(TranslateService);

	protected isLoading: WritableSignal<boolean> = signal(true);
	protected loadError: WritableSignal<boolean> = signal(false);
	protected content: WritableSignal<WebviewerAnnotationHelpCmsBlock> = signal(null);
	protected currentLang: WritableSignal<string> = signal("en");

	private langSubscription: Subscription;
	protected doNotShowAgainModel: ModelSignal<boolean> = model.required<boolean>();

	ngOnInit(): void {
		this.doNotShowAgainModel.set(WebviewerAnnotationHelpDialogComponent.shouldNotShowHelp());
		this.doNotShowAgainModel.subscribe((value) => {
			WebviewerAnnotationHelpDialogComponent.setDoNotShowHelp(value);
		});
		this.currentLang.set(
			this.translateService.getCurrentLang() || this.translateService.getFallbackLang()
		);
		this.langSubscription = this.translateService.onLangChange.subscribe((event) => {
			this.currentLang.set(event.lang);
		});
		this.cmsService
			.getCmsBlock<WebviewerAnnotationHelpCmsBlock>(CmsBlockKey.TM_WEBVIEWER_ANNOTATION_HELP)
			.subscribe({
				next: (block) => {
					this.isLoading.set(false);
					if (block?.activated && block.content) {
						this.content.set(block.content);
					} else {
						this.loadError.set(true);
					}
				},
				error: () => {
					this.loadError.set(true);
					this.isLoading.set(false);
				},
			});
	}

	ngOnDestroy(): void {
		this.langSubscription?.unsubscribe();
	}

	protected getLangValue(value: { de: string; en: string } | null | undefined): string {
		return this.currentLang() === "de" ? (value?.de ?? value?.en ?? "") : (value?.en ?? "");
	}

	/**
	 * Opens the annotation help dialog.
	 * @param dialog Material dialog service instance used for opening the component as dialog.
	 * @param dialogOptions Optional dialog config to override defaults (size, panelClass, etc.).
	 */
	public static openAnnotationHelpDialog(
		dialog: MatDialog,
		dialogOptions: MatDialogConfig<any> = {}
	): MatDialogRef<WebviewerAnnotationHelpDialogComponent> {
		return dialog.open(this, {
			minWidth: "400px",
			maxWidth: "800px",
			width: "70vw",
			...dialogOptions,
		});
	}

	/**
	 * Returns whether the help dialog should not be opened automatically.
	 */
	public static shouldNotShowHelp(): boolean {
		return localStorage.getItem(DO_NOT_SHOW_HELP_STORAGE_KEY) === "true";
	}

	/**
	 * Persists or clears the user preference for automatic help dialog opening.
	 * @param value True to disable automatic opening, false to re-enable it.
	 */
	public static setDoNotShowHelp(value: boolean): void {
		if (value) {
			localStorage.setItem(DO_NOT_SHOW_HELP_STORAGE_KEY, "true");
		} else {
			localStorage.removeItem(DO_NOT_SHOW_HELP_STORAGE_KEY);
		}
	}
}
